import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientsApi, enterpriseApi, policiesApi, submissionsApi } from "@/api/endpoints";
import type {
  CarrierRecord,
  CustomerProfile,
  Policy,
  Submission,
  SubmissionDetail,
  SubmissionStatus,
} from "@/api/types";
import { Modal } from "@/components/Modal";
import { PageHeader } from "@/components/PageHeader";
import { formatCurrency } from "@/utils/format";

const STATUSES: (SubmissionStatus | "all")[] = [
  "all",
  "draft",
  "submitted",
  "acknowledged",
  "in_review",
  "info_requested",
  "quoted",
  "bound",
  "declined",
  "withdrawn",
];

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  draft: { bg: "#e2e8f0", fg: "#334155" },
  submitted: { bg: "#dbeafe", fg: "#1d4ed8" },
  acknowledged: { bg: "#e0f2fe", fg: "#0369a1" },
  in_review: { bg: "#fef9c3", fg: "#854d0e" },
  info_requested: { bg: "#ffedd5", fg: "#9a3412" },
  quoted: { bg: "#dcfce7", fg: "#166534" },
  bound: { bg: "#bbf7d0", fg: "#14532d" },
  declined: { bg: "#fee2e2", fg: "#991b1b" },
  withdrawn: { bg: "#e5e7eb", fg: "#4b5563" },
};

function StatusChip({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] ?? STATUS_COLORS.draft;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.55rem",
        borderRadius: 999,
        fontSize: "0.78rem",
        fontWeight: 600,
        background: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

const inputStyle = {
  width: "100%",
  padding: "0.55rem 0.7rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
} as const;

function NewSubmissionModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<CustomerProfile[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policyId, setPolicyId] = useState("");
  const [carriers, setCarriers] = useState<CarrierRecord[]>([]);
  const [carrierId, setCarrierId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    enterpriseApi
      .carriers()
      .then((data) => setCarriers(data.items))
      .catch(() => setCarriers([]));
  }, [open]);

  useEffect(() => {
    if (customer || !search.trim()) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(() => {
      clientsApi
        .list(search.trim())
        .then((data) => active && setResults(data.items.slice(0, 8)))
        .catch(() => active && setResults([]));
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [search, customer]);

  useEffect(() => {
    if (!customer) {
      setPolicies([]);
      return;
    }
    policiesApi
      .list(undefined, customer.id)
      .then((data) => {
        setPolicies(data.items);
        if (data.items.length === 1) setPolicyId(data.items[0].id);
      })
      .catch(() => setPolicies([]));
  }, [customer]);

  function reset() {
    setCustomer(null);
    setSearch("");
    setResults([]);
    setPolicies([]);
    setPolicyId("");
    setCarrierId("");
    setNotes("");
    setError(null);
    setSaving(false);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!policyId) {
      setError("Select the policy to submit.");
      return;
    }
    if (!carrierId) {
      setError("Select the carrier to submit to.");
      return;
    }
    setSaving(true);
    try {
      await submissionsApi.create({
        carrier_id: carrierId,
        policy_id: policyId,
        notes: notes.trim() || undefined,
      });
      reset();
      onClose();
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create submission.");
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="New carrier submission" onClose={onClose} width="34rem">
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.85rem" }}>
        <div style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Client</span>
          {customer ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <strong>{customer.name}</strong>
              <button type="button" className="btn btn-secondary" onClick={() => { setCustomer(null); setPolicyId(""); }}>
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                style={inputStyle}
                placeholder="Search client by name, company, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {results.length > 0 ? (
                <div style={{ display: "grid", gap: "0.25rem" }}>
                  {results.map((item) => {
                    const name =
                      item.company_name ||
                      [item.first_name, item.last_name].filter(Boolean).join(" ") ||
                      item.email ||
                      "Unnamed";
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="btn btn-secondary"
                        style={{ justifyContent: "flex-start" }}
                        onClick={() => setCustomer({ id: item.id, name })}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
        </div>

        {customer ? (
          <label style={{ display: "grid", gap: "0.3rem" }}>
            <span className="field-label">Policy</span>
            <select style={inputStyle} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
              <option value="">Select policy…</option>
              {policies.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.policy_number} — {policy.product_line ?? "Unknown line"}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Carrier</span>
          <select style={inputStyle} value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
            <option value="">Select carrier…</option>
            {carriers.map((carrier) => (
              <option key={carrier.id} value={carrier.id}>
                {carrier.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "grid", gap: "0.3rem" }}>
          <span className="field-label">Notes (optional)</span>
          <textarea
            style={{ ...inputStyle, minHeight: "3.5rem", resize: "vertical" }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Target premium, remarks for the underwriter…"
          />
        </label>

        {error ? <div className="error-banner">{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SubmissionActions({
  submission,
  onChanged,
  onError,
}: {
  submission: SubmissionDetail;
  onChanged: (updated: SubmissionDetail) => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [premium, setPremium] = useState("");
  const [declineReason, setDeclineReason] = useState("");

  const canSend = submission.status === "draft" || submission.status === "info_requested";

  async function send() {
    setBusy("send");
    try {
      const result = await submissionsApi.send(submission.id);
      onChanged(result.submission);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setBusy(null);
    }
  }

  async function transition(status: SubmissionStatus) {
    setBusy(status);
    try {
      const premiumCents = premium.trim() ? Math.round(parseFloat(premium) * 100) : undefined;
      const updated = await submissionsApi.transition(submission.id, {
        status,
        note: note.trim() || undefined,
        premium_quoted_cents: status === "quoted" && premiumCents && premiumCents > 0 ? premiumCents : undefined,
        decline_reason: status === "declined" ? declineReason.trim() || undefined : undefined,
      });
      setNote("");
      onChanged(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(null);
    }
  }

  const transitions = submission.allowed_transitions.filter((status) => status !== "submitted");

  if (!canSend && transitions.length === 0) {
    return <p style={{ color: "var(--muted)", margin: 0 }}>This submission is closed.</p>;
  }

  return (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.6rem" }}>
        <input
          style={inputStyle}
          placeholder="Note for the timeline (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {transitions.includes("quoted") ? (
          <input
            style={inputStyle}
            inputMode="decimal"
            placeholder="Quoted premium ($)"
            value={premium}
            onChange={(e) => setPremium(e.target.value)}
          />
        ) : null}
      </div>
      {transitions.includes("declined") ? (
        <input
          style={inputStyle}
          placeholder="Decline reason (used if marking declined)"
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
        />
      ) : null}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {canSend ? (
          <button type="button" className="btn btn-primary" onClick={send} disabled={busy !== null}>
            {busy === "send" ? "Sending…" : "Send to carrier"}
          </button>
        ) : null}
        {transitions.map((status) => (
          <button
            key={status}
            type="button"
            className="btn btn-secondary"
            onClick={() => transition(status)}
            disabled={busy !== null}
          >
            {busy === status ? "Saving…" : `Mark ${status.replace(/_/g, " ")}`}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SubmissionsPage() {
  const [status, setStatus] = useState<string>("all");
  const [items, setItems] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function load() {
    submissionsApi
      .list({ status })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load submissions"));
  }

  useEffect(() => {
    load();
  }, [status]);

  useEffect(() => {
    if (!expandedId) {
      setDetail(null);
      return;
    }
    submissionsApi
      .get(expandedId)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load submission"));
  }, [expandedId]);

  function onDetailChanged(updated: SubmissionDetail) {
    setDetail(updated);
    load();
  }

  return (
    <>
      <PageHeader
        title="Submissions"
        subtitle="Carrier submissions from draft through quote, bind, or decline — every send and status change is recorded."
      />
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 220 }}>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value === "all" ? "All statuses" : value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          + New submission
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="card">
        {items.length === 0 ? (
          <div className="empty-state">
            No submissions{status !== "all" ? ` in ${status.replace(/_/g, " ")}` : ""} yet. Create one from a policy to
            start tracking carrier responses.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Submission</th>
                <th>Client</th>
                <th>Policy</th>
                <th>Carrier</th>
                <th>Product</th>
                <th>Status</th>
                <th>Quoted</th>
                <th>Carrier ref</th>
              </tr>
            </thead>
            <tbody>
              {items.map((submission) => (
                <>
                  <tr
                    key={submission.id}
                    onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <strong>{submission.submission_number}</strong>
                      <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                        ACORD {submission.acord_form ?? "—"}
                      </div>
                    </td>
                    <td>
                      {submission.customer_id ? (
                        <Link to={`/clients/${submission.customer_id}`} onClick={(e) => e.stopPropagation()}>
                          {submission.customer_name ?? "Client 360"}
                        </Link>
                      ) : (
                        submission.customer_name ?? "—"
                      )}
                    </td>
                    <td>
                      {submission.policy_id ? (
                        <Link to={`/policies/${submission.policy_id}`} onClick={(e) => e.stopPropagation()}>
                          {submission.policy_number ?? "Policy"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{submission.carrier_name ?? "—"}</td>
                    <td>{submission.product_line}</td>
                    <td>
                      <StatusChip status={submission.status} />
                    </td>
                    <td>
                      {submission.premium_quoted_cents != null ? formatCurrency(submission.premium_quoted_cents) : "—"}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>{submission.carrier_reference ?? "—"}</td>
                  </tr>
                  {expandedId === submission.id && detail ? (
                    <tr key={`${submission.id}-detail`}>
                      <td colSpan={8} style={{ background: "var(--surface-2, #f8fafc)" }}>
                        <div style={{ display: "grid", gap: "0.85rem", padding: "0.5rem 0.25rem" }}>
                          {detail.decline_reason ? (
                            <div className="error-banner">Declined: {detail.decline_reason}</div>
                          ) : null}
                          {detail.notes ? (
                            <p style={{ margin: 0, color: "var(--muted)" }}>{detail.notes}</p>
                          ) : null}
                          <SubmissionActions submission={detail} onChanged={onDetailChanged} onError={setError} />
                          <div>
                            <span className="field-label">Timeline</span>
                            <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.1rem", display: "grid", gap: "0.25rem" }}>
                              {detail.events.map((event) => (
                                <li key={event.id} style={{ fontSize: "0.85rem" }}>
                                  <strong>{event.to_status.replace(/_/g, " ")}</strong>
                                  {event.from_status ? ` (from ${event.from_status.replace(/_/g, " ")})` : ""}
                                  {" — "}
                                  {new Date(event.occurred_at).toLocaleString()}
                                  {event.note ? <span style={{ color: "var(--muted)" }}> · {event.note}</span> : null}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </>
              ))}
            </tbody>
          </table>
        )}
        {total > items.length ? (
          <p style={{ color: "var(--muted)", fontSize: "0.85rem", margin: "0.75rem 0 0" }}>
            Showing {items.length} of {total}.
          </p>
        ) : null}
      </div>

      <NewSubmissionModal open={creating} onClose={() => setCreating(false)} onCreated={load} />
    </>
  );
}
